import {gql} from '@apollo/client'
import {ORDER_FIELDS, PREVIEW_FIELDS} from './fragments'

export const UserQuery = gql`
  query User($email: String!, $password: String!) {
    getUser(email: $email, password: $password) {
      id
      name
      first_name
      last_name
      email
      password
      role_id
      role_name
      customer_service {
        search_orders
        change_email_address
        upgrade_shipping_method
        issue_refund_with_fixed_amount
        issue_refund_with_percent_amount
        extend_request_window
        copy_magic_link
        manually_issue_replacement_refund
      }
      graphic_design {
        export_new_orders
        export_incomplete_orders
        export_pending_orders
        export_rejected_orders
        upload_pending_orders
        search_orders
        view_action_history
        view_order_action_history
        export_new_mockups
        export_incomplete_mockups
        export_pending_mockups
        upload_pending_mockups
        search_mockups
        view_mockups_action_history
      }
      refund_requests {
        search_requests
        view_new_requests
        view_closed_requests
        approve_requests
        reject_requests
        view_action_history
      }
      accounts {
        view_accounts
        add_account
        add_business_days
        add_rush_days
        add_wholesale_days
        change_first_name
        change_last_name
        change_email_address
        change_password
        change_permissions
        delete_account
        manage_roles
      }
    }
  }
`

export const OrderQuery = gql`
  query Order($id: String!, $email: String!) {
    getOrder(id: $id, email: $email) {
      id
      source_id
      email
      shipping_method
      quantity_total
      weight_total
      product_total
      shipping_total
      handling_total
      tax_total
      discount_total
      order_total
      cc_number_masked
      cc_exp
      processor_response
      payment_type
      payment_status
      processor_balance
      refund_total
      # customer_id
      email_count
      ip_address
      tag_color
      source_name
      source_id
      folder_id
      date_added
      date_updated
      shipping {
        first_name
        last_name
        company
        address1
        address2
        city
        state
        postal_code
        country
        phone
      }
      customer {
        first_name
        last_name
        company
        address1
        address2
        city
        state
        postal_code
        country
        phone
      }
      order_shipments {
        id
        order_id
        store_id
        tracking_number
        carrier_code
        shipment_method
        weight
        cost
        status
        tracking_url
        label_format
        label_image
        order_items
        print_status
        cart_shipment_id
        date_shipped
        date_added
      }
      checkout_data {
        key
        value
      }
      order_metadata {
        key
        value
      }
      order_items {
        id
        name
        price
        quantity
        weight
        code
        delivery_type
        category_code
        variation_list {
          key
          value
        }
        metadata {
          key
          value
        }
      }
    }
  }
`

export const OrderByIdNumber = gql`
  ${ORDER_FIELDS}
  query OrderByIdNumber($number: String!) {
    getOrderByNumber(number: $number) {
      ...OrderFields
    }
  }
`

export const OrdersBySearchQuery = gql`
  ${ORDER_FIELDS}
  query Orders($criteria: String!, $search: String!) {
    getOrders(criteria: $criteria, search: $search) {
      ...OrderFields
    }
  }
`

export const OrdersByTikTokOrderIdQuery = gql`
  ${ORDER_FIELDS}
  query OrdersByTikTokOrderId($tiktokOrderId: String!) {
    getOrdersByTikTokOrderId(tiktokOrderId: $tiktokOrderId) {
      ...OrderFields
    }
  }
`

export const OverdueOrdersQuery = gql`
  ${ORDER_FIELDS}
  query OverdueOrders {
    getOverdueOrders {
      ...OrderFields
    }
  }
`;

export const OrderBySearchQuery = gql`
  ${ORDER_FIELDS}
  query Orders($criteria: String!, $search: String!, $folder: String) {
    getOrder(criteria: $criteria, search: $search, folder: $folder) {
      ...OrderFields
    }
  }
`
export const PreviewBySearchQuery = gql`
  ${ORDER_FIELDS}
  query Previews($criteria: String!, $search: String!, $folder: String) {
    getPreview(criteria: $criteria, search: $search, folder: $folder) {
      ...OrderFields
    }
  }
`
export const OverduePreviewsQuery = gql`
  ${ORDER_FIELDS}
  query OverduePreviews {
    getOverduePreviews {
      ...OrderFields
    }
  }
`;

export const PORTAL_STATUS = gql`
  query PORTAL_STATUS($count: Int!) {
    getPortalStatus(count: $count) {
      portal_history {
        count
        file
        created_at
        email
        type
      }
    }
  }
`

export const PREVIEW_STATUS = gql`
  query PREVIEW_STATUS($count: Int!) {
    getPreviewStatus(count: $count) {
      preview_history {
        count
        file
        created_at
        email
        status
      }
    }
  }
`

export const REQUEST_HISTORY = gql`
  query REQUEST_HISTORY($order_id: String, $order_source_id: String) {
    getRequestHistory(order_id: $order_id, order_source_id: $order_source_id) {
      id
      action
      created_at
      reason
    }
  }
`

export const REFUND_REQUEST_HISTORY = gql`
   query REFUND_REQUEST_HISTORY($order_id: String, $order_source_id: String) {
      getRefundRequestHistory(order_id: $order_id, order_source_id: $order_source_id) {
        action
        created_at
        rejectReason
        new_order_source_id
        order_source_id
        order_id
        refund_amount
      }
    }
`

export const REFUND_REQUESTS = gql`
  query REFUND_REQUESTS($status: String) {
    getRefundRequests(status: $status) {
      new_requests {
        id
        created_at
        updated_at
        first_name
        last_name
        city
        country
        state
        order_id
        order_source_id
        quantity
        reason
        solution
        status
        uploaded_image
      }

      closed_requests {
        id
        created_at
        updated_at
        first_name
        last_name
        city
        country
        state
        order_id
        order_source_id
        quantity
        reason
        solution
        status
        uploaded_image
      }
    }
  }
`

export const GET_USERS = gql`
  query GET_USERS($id: String) {
    getUsers(id: $id) {
      active_users {
        id
        name
        first_name
        last_name
        email
        password
        role_id
        role_name
        customer_service {
          search_orders
          change_email_address
          upgrade_shipping_method
          issue_refund_with_fixed_amount
          issue_refund_with_percent_amount
          extend_request_window
          copy_magic_link
          manually_issue_replacement_refund
        }
        graphic_design {
          export_new_orders
          export_incomplete_orders
          export_pending_orders
          export_rejected_orders
          upload_pending_orders
          search_orders
          view_action_history
          view_order_action_history
          export_new_mockups
          export_incomplete_mockups
          export_pending_mockups
          upload_pending_mockups
          search_mockups
          view_mockups_action_history
        }
        refund_requests {
          search_requests
          view_new_requests
          view_closed_requests
          approve_requests
          reject_requests
          view_action_history
        }
        accounts {
          view_accounts
          add_account
          add_business_days
          add_rush_days
          add_wholesale_days
          change_first_name
          change_last_name
          change_email_address
          change_password
          change_permissions
          delete_account
          manage_roles
        }
      }
      inactive_users {
        id
        name
        first_name
        last_name
        email
        password
        role_id
        role_name
        customer_service {
          search_orders
          change_email_address
          upgrade_shipping_method
          issue_refund_with_fixed_amount
          issue_refund_with_percent_amount
          extend_request_window
          copy_magic_link
          manually_issue_replacement_refund
        }
        graphic_design {
          export_new_orders
          export_incomplete_orders
          export_pending_orders
          export_rejected_orders
          upload_pending_orders
          search_orders
          view_action_history
          view_order_action_history
          export_new_mockups
          export_incomplete_mockups
          export_pending_mockups
          upload_pending_mockups
          search_mockups
          view_mockups_action_history
        }
        refund_requests {
          search_requests
          view_new_requests
          view_closed_requests
          approve_requests
          reject_requests
          view_action_history
        }
        accounts {
          view_accounts
          add_account
          add_business_days
          add_rush_days
          add_wholesale_days
          change_first_name
          change_last_name
          change_email_address
          change_password
          change_permissions
          delete_account
          manage_roles
        }
      }
    }
  }
`

export const GET_DAYS = gql`
  query GET_DAYS($id: String) {
    getDays(id: $id) {
      days {
        from
        to
        minimumShipDate
        maximumShipDate
        minimumDeliveryDate
        maximumDeliveryDate
      }
    }
  }
`

export const GET_RUSH = gql`
  query GET_RUSH($id: String) {
    getRush(id: $id) {
      rush {
        from
        to
        minimumShipDate
        maximumShipDate
        minimumDeliveryDate
        maximumDeliveryDate
      }
    }
  }
`

export const APPROVE_REQUEST = gql`
  mutation APPROVE_REQUEST($request_id: String, $amount: String!) {
    approveRequest(request_id: $request_id, amount: $amount) {
      request {
        id
        created_at
        updated_at
        first_name
        last_name
        city
        country
        state
        order_id
        order_source_id
        quantity
        reason
        solution
        status
        uploaded_image
      }

      history {
        id
        action
        created_at
        reason
      }
    }
  }
`

export const REJECT_REQUEST = gql`
  mutation REJECT_REQUEST($request_id: String, $rejectReason: String) {
    rejectRequest(request_id: $request_id, rejectReason: $rejectReason) {
      request {
        id
        created_at
        updated_at
        first_name
        last_name
        city
        country
        state
        order_id
        order_source_id
        quantity
        reason
        solution
        status
        uploaded_image
      }

      history {
        id
        action
        created_at
        reason
      }
    }
  }
`

export const ORDERS_COUNT = gql`
  query ORDERS_COUNT($criteria: String!, $search: String!) {
    getOrdersCount(criteria: $criteria, search: $search) {
      new_orders_count
      incomplete_orders_count
      pending_orders_count
      rejected_orders_count
    }
  }
`

export const PREVIEWS_COUNT = gql`
  query PreviewsCount {
    getPreviewsCount {
      new_previews_count
      incomplete_previews_count
      pending_previews_count
    }
  }
`;

export const OrderByIdQuery = gql`
  ${ORDER_FIELDS}
  query OrderById($id: String!) {
    getOrderById(id: $id) {
      ...OrderFields
    }
  }
`

export const PortalOrderByIdQuery = gql`
  query OrderById($id: String!, $email: String!) {
    getOrderById(id: $id, email: $email) {
      id
      source_id                                                                                         
      email
      order_items {
        id
        name
        variation_list {
          key
          value
        }
      }
    }
  }
`

export const GET_COLLABS = gql`
  query GET_COLLABS($search: String, $criteria: String) {
    getCollabs(search: $search, criteria: $criteria) {
      collabs {
        id
        first_name
        last_name
        email
        typeofcollabs
        compensation
        amount_or_discount
        deliverable_number_or_discount_offer
        deliverable_platforms
        deliverable_types
        accounts
        status
        links_to_accounts {
          key
          value
        }
        created_at
        updated_at
      }
    }
  }
`
export const UPDATE_EMAIL = gql`
  ${ORDER_FIELDS}
  mutation UpdateEmail(
    $email: String!
    $orderId: String!
  ) {
    updateEmail(email: $email, orderId: $orderId) {
      deliverability
      status
      message
      execution_time
      order {
        ...OrderFields
      }
    }
  }
`

export const UPDATE_SHIPMENT = gql`
  ${ORDER_FIELDS}
  mutation UpdateShipment(
    $orderId: String!
    $carrier: String!
  ) {
    updateShipment(orderId: $orderId, carrier: $carrier) {
      status
      message
      execution_time
      order {
        ...OrderFields
      }
    }
  }
`

export const CANCEL_ORDER = gql`
  mutation CancelOrder(
    $orderId: String!
    $folderId: Int!
    $options: CancelOrderOptions
  ) {
    cancelOrder(
      orderId: $orderId
      folderId: $folderId
      options: $options
    ) {
      status
      message
      order {
        folder_id
      }
    }
  }
`

export const UPDATE_ORDER_ITEM_METADATA = gql`
  ${ORDER_FIELDS}
  mutation UPDATE_ORDER_ITEM_METADATA(
    $order_number: String!
    $itemIndex: Int!
    $key: String!
    $value: String!
  ) {
    updateOrderItemMetaDataByOrderNumber(
      order_number: $order_number,
      itemIndex: $itemIndex,
      key: $key,
      value: $value
    ) {
      status
      message
      execution_time
      order {
        ...OrderFields
      }
    }
  }
`

export const UPDATE_PREVIEW_ITEM_METADATA = gql`
 ${ORDER_FIELDS}
  mutation UpdatePreviewItemMetaData(
    $order_number: String!, 
    $itemIndex: Int!, 
    $key: String!, 
    $value: String!
  ) {
    updatePreviewItemMetaData(
      order_number: $order_number, 
      itemIndex: $itemIndex, 
      key: $key, 
      value: $value
    ) {
      status
      message
      execution_time
      order {
        ...OrderFields
      }
    }
  }
`;

export const CHECK_AND_MOVE_ORDERS = gql`
  mutation CheckAndMoveOrders($previewNumbers: [String!]!, $email: String!) {
    checkAndMoveOrders(previewNumbers: $previewNumbers, email: $email) {
      status
      message
      movedOrders
      orderResults {
        previewNumber
        status
        reason
        destinationFolder
        folderId
      }
    }
  }
`;

export const UPDATE_ORDER_ITEM_VARIATION = gql`
  ${ORDER_FIELDS}
  mutation UPDATE_ORDER_ITEM_VARIATION(
    $orderId: String!
    $orderItemId: String!
    $key: String!
    $value: String!
  ) {
    updateOrderItemVariation(
      orderId: $orderId,
      orderItemId: $orderItemId,
      key: $key,
      value: $value
    ) {
      status
      message
      execution_time
      order {
        ...OrderFields
      }
    }
  }
`

export const UPDATE_PREVIEW_ITEM_METADATA_BY_ORDER_ITEM_ID = gql`
  ${ORDER_FIELDS}
  mutation UPDATE_PREVIEW_ITEM_METADATA_BY_ORDER_ITEM_ID(
    $orderId: String!
    $orderItemId: String!
    $key: String!
    $value: String!
  ) {
    updatePreviewItemMetadataByOrderItemID(
      orderId: $orderId,
      orderItemId: $orderItemId,
      key: $key,
      value: $value
    ) {
      status
      message
      execution_time
      order {
        ...OrderFields
      }
    }
  }
`

export const EXPORT_ORDERS = gql`
  mutation EXPORT_ORDERS(
    $type: String!
    $email: String!
    $src_folder: String!
    $dest_folder: String
  ) {
    exportOrders(type: $type, email: $email, src_folder: $src_folder, dest_folder: $dest_folder) {
      status
      csv_link
    }
  }
`
export const EXPORT_SINGLE_ORDER = gql`
  mutation EXPORT_SINGLE_ORDER(
    $order: String!
    $email: String!
    $dest_folder: String
  ) {
    exportSingleOrder(order: $order, email: $email, dest_folder: $dest_folder) {
      status
      csv_link
    }
  }
`
export const EXTEND_ORDER = gql`
  ${ORDER_FIELDS}
  mutation ExtendOrder(
    $orderId: String!
    $date: String!
  ) {
    extendOrder(orderId: $orderId, date: $date) {
      status
      message
      execution_time
      order {
        ...OrderFields
      }
    }
  }
`

export const EXTEND_REVIEW_WINDOW = gql`
  ${ORDER_FIELDS}
  mutation ExtendReviewWindow($orderId: String!) {
    extendReviewWindow(orderId: $orderId) {
      status
      message
      execution_time
      order {
        ...OrderFields
      }
    }
  }
`

export const ADD_PHOTO = gql`
  mutation UploadPhoto($file: Upload!, $orderId: String!, $itemId: Int!) {
    uploadPhoto(file: $file, orderId: $orderId, itemId: $itemId) {
      status
      message
      execution_time
      url
      orderId
      itemId
    }
  }
`

export const UPLOAD_FILE_TO_S3 = gql`
  mutation UploadFileToS3($folderKey: String!, $fileName: String!, $contentType: String!) {
    uploadFileToS3(folderKey: $folderKey, fileName: $fileName, contentType: $contentType) {
      status
      message
      url
      uploadUrl
      key
      bucket
    }
  }
`

export const ISSUE_REFUND = gql`
  ${ORDER_FIELDS}
  mutation IssueRefund(
    $orderId: String!
    $amount: String!
    $type: String!
    $reason: String!
  ) {
    issueRefund(orderId: $orderId, amount: $amount, type: $type, reason: $reason) {
      status
      message
      execution_time
      order {
        ...OrderFields
      }
    }
  }
`

export const SAVE_BATCH_HISTORY = gql`
  mutation saveBatchHistory(
    $count: Int!
    $email: String!
    $folder: String!
  ) {
    saveBatchHistory(count: $count, email: $email, folder: $folder) {
      status
      message
    }
  }
`
export const CHECK_STATUS = gql`
  query DeliveryStatus(
    $carrier: String!
    $tracking_number: String!
  ) {
    deliveryStatus(
      carrier: $carrier
      tracking_number: $tracking_number
    ) {
      status
      attempt
      last_detail {
        datetime
        status
        message
      }
    }
  }
`
export const CREATE_NEW_USER = gql`
  mutation CREATE_NEW_USER(
    $data: UserData
  ) {
    createNewUser(data: $data) {
      status
    }
  }
`

export const UPDATE_USER = gql`
  mutation UPDATE_USER(
    $data: UserData
  ) {
    updateUser(data: $data) {
      status
    }
  }
`

export const DELETE_USER = gql`
  mutation DELETE_USER(
    $id: String
  ) {
    deleteUser(id: $id) {
      status
    }
  }
`

export const GET_ROLES = gql`
  query GET_ROLES($id: String) {
    getRoles(id: $id) {
      active_roles {
        id
        role_name
        description
        customer_service {
          search_orders
          change_email_address
          upgrade_shipping_method
          issue_refund_with_fixed_amount
          issue_refund_with_percent_amount
          extend_request_window
          copy_magic_link
          manually_issue_replacement_refund
        }
        graphic_design {
          export_new_orders
          export_incomplete_orders
          export_pending_orders
          export_rejected_orders
          upload_pending_orders
          search_orders
          view_action_history
          view_order_action_history
          export_new_mockups
          export_incomplete_mockups
          export_pending_mockups
          upload_pending_mockups
          search_mockups
          view_mockups_action_history
        }
        refund_requests {
          search_requests
          view_new_requests
          view_closed_requests
          approve_requests
          reject_requests
          view_action_history
        }
        accounts {
          view_accounts
          add_account
          add_business_days
          add_rush_days
          add_wholesale_days
          change_first_name
          change_last_name
          change_email_address
          change_password
          change_permissions
          delete_account
          manage_roles
        }
        status
        created_at
        updated_at
      }
      inactive_roles {
        id
        role_name
        description
        customer_service {
          search_orders
          change_email_address
          upgrade_shipping_method
          issue_refund_with_fixed_amount
          issue_refund_with_percent_amount
          extend_request_window
          copy_magic_link
          manually_issue_replacement_refund
        }
        graphic_design {
          export_new_orders
          export_incomplete_orders
          export_pending_orders
          export_rejected_orders
          upload_pending_orders
          search_orders
          view_action_history
          view_order_action_history
          export_new_mockups
          export_incomplete_mockups
          export_pending_mockups
          upload_pending_mockups
          search_mockups
          view_mockups_action_history
        }
        refund_requests {
          search_requests
          view_new_requests
          view_closed_requests
          approve_requests
          reject_requests
          view_action_history
        }
        accounts {
          view_accounts
          add_account
          add_business_days
          add_rush_days
          add_wholesale_days
          change_first_name
          change_last_name
          change_email_address
          change_password
          change_permissions
          delete_account
          manage_roles
        }
        status
        created_at
        updated_at
      }
    }
  }
`

export const CREATE_ROLE = gql`
  mutation CREATE_ROLE(
    $data: RoleData
  ) {
    createRole(data: $data) {
      status
    }
  }
`

export const UPDATE_ROLE = gql`
  mutation UPDATE_ROLE(
    $data: RoleData
  ) {
    updateRole(data: $data) {
      status
    }
  }
`

export const DELETE_ROLE = gql`
  mutation DELETE_ROLE(
    $id: String
  ) {
    deleteRole(id: $id) {
      status
    }
  }
`

export const CREATE_NEW_DAY = gql`
  mutation CREATE_NEW_DAY(
    $minimumShipDate: String!, $maximumShipDate: String!, $minimumDeliveryDate: String!, $maximumDeliveryDate: String!
  ) {
    createNewDay(
      minimumShipDate: $minimumShipDate, 
      maximumShipDate: $maximumShipDate, 
      minimumDeliveryDate: $minimumDeliveryDate, 
      maximumDeliveryDate: $maximumDeliveryDate
    ) {
      status
    }
  }
`

export const CREATE_NEW_RUSH = gql`
  mutation CREATE_NEW_RUSH(
    $minimumShipDate: String!, 
    $maximumShipDate: String!, 
    $minimumDeliveryDate: String!, 
    $maximumDeliveryDate: String!
  ) {
    createNewRush(
      minimumShipDate: $minimumShipDate, 
      maximumShipDate: $maximumShipDate, 
      minimumDeliveryDate: $minimumDeliveryDate, 
      maximumDeliveryDate: $maximumDeliveryDate
    ) {
      status
    }
  }
`

export const GET_WHOLESALE = gql`
  query GET_WHOLESALE($id: String) {
    getWholesale(id: $id) {
      wholesale {
        from
        to
        minimumShipDate
        maximumShipDate
        minimumDeliveryDate
        maximumDeliveryDate
      }
    }
  }
`

export const CREATE_NEW_WHOLESALE = gql`
  mutation CREATE_NEW_WHOLESALE(
    $minimumShipDate: String!, 
    $maximumShipDate: String!, 
    $minimumDeliveryDate: String!, 
    $maximumDeliveryDate: String!
  ) {
    createNewWholesale(
      minimumShipDate: $minimumShipDate, 
      maximumShipDate: $maximumShipDate, 
      minimumDeliveryDate: $minimumDeliveryDate, 
      maximumDeliveryDate: $maximumDeliveryDate
    ) {
      status
    }
  }
`

export const GET_DIGITAL_PREVIEWS = gql`
  query GetDigitalPreviews($status: String, $email: String) {
    getDigitalPreviews(status: $status, email: $email) {
      id
      previews {
        created_at
        crop
        photo_link
        status
        identifier
        flag
      }
    }
  }
`

export const EXPORT_PREVIEWS = gql`
  mutation ExportPreviews($folder_id: String, $dest_folder_id: String, $email: String) {
    exportPreviews(folder_id: $folder_id, dest_folder_id: $dest_folder_id, email: $email) {
      status
      csv_link
    }
  }
`;

export const EXPORT_SINGLE_PREVIEW = gql`
  mutation EXPORT_SINGLE_PREVIEW(
    $order: String!
    $email: String!
    $dest_folder: String
  ) {
    exportSinglePreview(order: $order, email: $email, dest_folder: $dest_folder) {
      status
      csv_link
    }
  }
`

export const SAVE_PREVIEW_BATCH_HISTORY = gql`
  mutation SavePreviewBatchHistory($fileDataArray: [FileDataInput!]!, $folderKey: String!, $userEmail: String!) {
    savePreviewBatchHistory(fileDataArray: $fileDataArray, folderKey: $folderKey, userEmail: $userEmail) {
      status
      message
    }
  }
`;

export const SEND_EMAIL = gql`
  mutation sendEmail($templateId: String!, $recipient: String!, $dynamicData: [EmailData!]!) {
    sendEmail(templateId: $templateId, recipient: $recipient, dynamicData: $dynamicData) {
      status
      message
    }
  }
`;

export const CREATE_SHOPIFY_CUSTOMER = gql`
  mutation CreateShopifyCustomer($email: String!) {
    createShopifyCustomer(email: $email) {
      id
      email
      createdAt
    }
  }
`;

export const FLAG_PREVIEW = gql`
  mutation flagPreview(
    $identifier: String!, 
    $createdAt: String!, 
    $flagValue: String, 
    $email: String!
  ) {
    flagPreview(
      identifier: $identifier, 
      createdAt: $createdAt, 
      flagValue: $flagValue, 
      email: $email
    ) {
      status
      message
    }
  }
`;

export const GENERATE_GIF_FROM_LINE_ITEMS = gql`
  mutation GenerateGifFromLineItems($orderIds: [String!]!, $source: String!) {
    generateGifFromLineItems(orderIds: $orderIds, source: $source) {
      status
      message
      processedIds
    }
  }
`

export const MOVE_BATCH_UPLOAD_ORDERS = gql`
  mutation MOVE_BATCH_UPLOAD_ORDERS($orderNumbers: [String!]!) {
    moveBatchUploadOrders(orderNumbers: $orderNumbers) {
      status
      moved {
        source_id
        status
      }
    }
  }
`

export const GET_GRAPHIC_ORDERS = gql`
  ${ORDER_FIELDS}
  query GetGraphicOrders {
    getGraphicOrders {
      new_orders {
        ...OrderFields
      }
      incomplete_orders {
        ...OrderFields
      }
      pending_orders {
        ...OrderFields
      }
      rejected_orders {
        ...OrderFields
      }
    }
  }
`

export const GET_GRAPHIC_PREVIEWS = gql`
  ${ORDER_FIELDS}
  query GetGraphicPreviews {
    getGraphicPreviews {
      new_previews {
        ...OrderFields
      }
      incomplete_previews {
        ...OrderFields
      }
      pending_previews {
        ...OrderFields
      }
    }
  }
`